import orderStyles from '@crm/styles/order.module.css';
import {capitalize, smoothScrollToTop} from '@crm/utils/helpers';
import React, {useEffect} from 'react';
import {useAppDispatch, useAppSelector} from '@crm/services/store';
import {
    selectWindowCleaningAddress,
    selectWindowCleaningAvailability,
    selectWindowCleaningFrequencyId,
    selectWindowCleaningGeneralNote,
    selectWindowCleaningMaxPage,
    selectWindowCleaningPaymentType,
    selectWindowCleaningResidenceType,
    selectWindowCleaningSecondaryGlazing,
    selectWindowCleaningServiceId,
    selectWindowCleaningWindows
} from '@crm/services/window-cleaning-selectors';
import {housekeeprApi} from '@crm/api/housekeepr-api';
import {skipToken} from '@reduxjs/toolkit/query';
import {windowCleaningActions, WindowCleaningPage} from '@crm/services/window-cleaning-slice';
import Image from 'next/image';
import {MobilepayBlue, PaymentCardOptions} from '@crm/utils/images';
import CheckBoxCircle from '@crm/components/check-box/check-box-circle';
import {routes} from '@crm/routes';
import Typography from '@crm/components/typography';
import {WindowCleaningRedeemCode} from '@crm/components/window-cleaning/window-cleaning-redeem-code';
import createEventV2 from '@crm/utils/gtagv2';
import {authSelectors} from "@crm/services/auth-slice";
import {WindowCleaningServicePriceRequest} from "@crm/api/models/requests/housekeepr-api";
import {cleaningActions} from "@crm/services/cleaning-slice";

type WindowCleaningPaymentCardProps = { hidden?: boolean };

export const WindowCleaningPaymentCard = (props: WindowCleaningPaymentCardProps) => {
    const dispatch = useAppDispatch();

    const maxPage = useAppSelector(selectWindowCleaningMaxPage);

    const generalNote = useAppSelector(selectWindowCleaningGeneralNote);
    const selectedPaymentType = useAppSelector(selectWindowCleaningPaymentType);

    const selectedAddress = useAppSelector(selectWindowCleaningAddress);
    const selectedServiceId = useAppSelector(selectWindowCleaningServiceId);
    const selectedResidenceType = useAppSelector(selectWindowCleaningResidenceType);
    const selectedWindowTypes = useAppSelector(selectWindowCleaningWindows);
    const selectedFrequencyId = useAppSelector(selectWindowCleaningFrequencyId);
    const selectedAvailability = useAppSelector(selectWindowCleaningAvailability);
    const hasSecondaryGlazing = useAppSelector(selectWindowCleaningSecondaryGlazing);
    const verifiedCode = useAppSelector(state => state.windowCleaning.verifiedCode);
    const authenticated = useAppSelector(authSelectors.selectAuthenticated);
    const canPay = useAppSelector(state => state.windowCleaning.canPay);

    const [acceptedTOS, setAcceptedTOS] = React.useState(false);

    const {
        data: residenceTypesResult
    } = housekeeprApi.useGetResidenceTypesQuery();

    const {
        data: serviceResult
    } = housekeeprApi.useGetServiceQuery(selectedServiceId ?? skipToken);

    const isInside = serviceResult?.name.toLowerCase().replace('vinduespudsning', '').includes('ind');

    const {
        data: paymentMethodAvailabilityResult
    } = housekeeprApi.useGetPaymentAvailabilityQuery(authenticated ? undefined : skipToken);

    const {data: addressResult} = housekeeprApi.useRegisterAddressQuery(
        selectedAddress ? {darId: selectedAddress.adresse.id} : skipToken
    );

    const frequency = serviceResult?.frequencies.find(frequency => frequency.id == selectedFrequencyId);
    const residenceType = residenceTypesResult?.find(residenceType => residenceType.type == selectedResidenceType);

    const canSearchPrice = !!selectedServiceId && !!selectedResidenceType && selectedWindowTypes.filter(window => window.count > 0).length > 0 && !!addressResult;

    const useCode = !!verifiedCode;

    const {data: codeResult} = housekeeprApi.useCheckCodeQuery(
        useCode && selectedServiceId && selectedFrequencyId ? {
            code: verifiedCode,
            service_id: selectedServiceId,
            frequency_id: selectedFrequencyId,
        } : skipToken
    );

    const priceRequest = canSearchPrice ? {
        service_id: selectedServiceId,
        address_id: addressResult.id,
        frequency_id: selectedFrequencyId,
        has_secondary_glazing: hasSecondaryGlazing,
        residence_type: selectedResidenceType,
        windows: selectedWindowTypes,
    } as WindowCleaningServicePriceRequest : undefined;

    const {
        data: priceResult,
        isFetching: isFetchingPrice,
    } = housekeeprApi.useGetServicePriceQuery(priceRequest ?? skipToken);

    const priceWithoutVAT = priceResult ?? 0;
    const priceWithVAT = priceWithoutVAT * 1.25;
    const discount = useCode ? priceWithVAT * ((codeResult?.discount_code?.percent_off ?? 0) / 100) + (codeResult?.discount_code?.value ?? 0) : 0;
    const priceWithVATAndDiscount = priceWithVAT - discount;
    const frequencyDiscount = priceWithVATAndDiscount * ((frequency?.discount ?? 0) / 100);
    const finalPrice = priceWithVATAndDiscount - frequencyDiscount;

    const formattedPrice = finalPrice.toLocaleString('da-DK', {
        style: 'currency',
        currency: 'DKK'
    });

    useEffect(() => {
        if (!canPay && paymentMethodAvailabilityResult?.available) {
            dispatch(windowCleaningActions.setCanPay(true));
            if (!selectedPaymentType) {
                dispatch(windowCleaningActions.setPaymentType('card'));
            }
        }
    }, [authenticated, canPay, dispatch, paymentMethodAvailabilityResult?.available, selectedPaymentType]);

    return <div
        className={props.hidden ? orderStyles.hidden : orderStyles.cardWrapper}
    >
        <div
            className={orderStyles.card}
        >
            <div
                className={orderStyles.orderSection}
            >
                <h1 className={orderStyles.cardH1}>
                    Bestillingsoversigt
                </h1>
                <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                    Tjek at alt er som det skal være. Hvis ikke, kan du klikke på et punkt for at redigere.
                </p>
                <div
                    className={`${orderStyles.orderSectionContent} ${orderStyles.flexRow} ${orderStyles.flexWrap} ${orderStyles.flexCrossStretch}`}>
                    <div
                        className={`${orderStyles.overviewCard} cursor-pointer`}
                        onClick={() => {
                            dispatch(windowCleaningActions.setPage(WindowCleaningPage.HOME));
                            document.querySelector('#address-content')?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }}
                    >
                        <h2>Adresse</h2>
                        <p>
                            {selectedAddress?.tekst ?? 'Ikke valgt endnu'}
                        </p>
                    </div>
                    <div
                        className={`${orderStyles.overviewCard} cursor-pointer`}
                        onClick={() => {
                            dispatch(windowCleaningActions.setPage(WindowCleaningPage.HOME));
                            document.querySelector('#residence-type-content')?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }}
                    >
                        <h2>Boligtype</h2>
                        <p>
                            {residenceType?.description ?? 'Ikke valgt endnu'}
                        </p>
                    </div>

                    <div
                        className={`${orderStyles.overviewCard} cursor-pointer`}
                        onClick={() => {
                            dispatch(windowCleaningActions.setPage(WindowCleaningPage.HOME));
                            document.querySelector('#service-content')?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }}
                    >
                        <h2>Vinduespudsningstype</h2>
                        <p>
                            {serviceResult?.name ?? (selectedServiceId ? `Service #${selectedServiceId}` : 'Ikke valgt endnu')}
                        </p>
                    </div>

                    <div
                        className={`${orderStyles.overviewCard} cursor-pointer`}
                        onClick={() => {
                            dispatch(windowCleaningActions.setPage(WindowCleaningPage.HOME));
                            document.querySelector('#glazing-content')?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }}
                    >
                        <h2>Forsats på vinduerne</h2>
                        <p>
                            {hasSecondaryGlazing ? 'Ja' : 'Nej'}
                        </p>
                    </div>
                    <div
                        className={`${orderStyles.overviewCard} cursor-pointer`}
                        onClick={() => {
                            dispatch(windowCleaningActions.setPage(WindowCleaningPage.WINDOWS));
                            document.querySelector('#window-types-content')?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }}
                    >
                        <h2>Antal vinduer</h2>
                        <p>
                            {`${selectedWindowTypes.map(windowType => windowType.count).reduce((a, b) => a + b, 0)} valgt`}
                        </p>
                    </div>
                    <div
                        className={`${orderStyles.overviewCard} cursor-pointer`}
                        onClick={() => {
                            dispatch(windowCleaningActions.setPage(WindowCleaningPage.TIME));
                            document.querySelector('#frequency-content')?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }}
                    >
                        <h2>Hvor ofte?</h2>
                        <p>
                            {frequency?.nicified_string ?? 'Ikke valgt endnu'}
                        </p>
                    </div>
                    <div
                        className={`${orderStyles.overviewCard} cursor-pointer`}
                        onClick={() => {
                            dispatch(windowCleaningActions.setPage(WindowCleaningPage.TIME));
                            document.querySelector('#availability-content')?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center'
                            });
                        }}
                    >
                        <h2>
                            {frequency?.every !== 0
                                ? 'Tidsrum for ankomst ved første besøg'
                                : 'Tidsrum for ankomst'
                            }
                        </h2>
                        <p>
                            {selectedAvailability
                                ? `${capitalize(new Date(selectedAvailability.date).toLocaleDateString('da-DK', {dateStyle: 'medium'}))}, ${selectedAvailability.time_slot_range}`
                                : 'Ikke valgt endnu'
                            }
                        </p>
                    </div>
                </div>
            </div>
            <div
                className={orderStyles.orderSection}
            >
                <h1 className={orderStyles.cardH1}>
                    Kampagner & Rabatkoder
                </h1>
                <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                    Hvis du har en kampagnekode, referralkode eller rabatkode, kan du indtaste den her.
                    Bemærk referralkoder kun kan benyttes på abonnementer.
                </p>
                <WindowCleaningRedeemCode/>
            </div>
            <div
                className={orderStyles.orderSection}
            >
                <h1 className={orderStyles.cardH1}>
                    Vigtigt at vide
                </h1>
                <ul className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                    <li>• Vi tager kun de vinduer, vi har adgang til.</li>
                    {isInside && <li>• Du skal være hjemme, når vi ankommer.</li>}
                    {isInside
                        ? <li>• Udendørs bruger vi rentvandsanlæg op til 3. sal.</li>
                        : <li>• Vi bruger rentvandsanlæg op til 3. sal.</li>
                    }
                    {!isInside &&
                        <li>• Du skal være hjemme, hvis vi skal tage vinduer over 3. sal eller hvis du har kalkmalede
                            vinduer.</li>
                    }
                    <li>• Luk vinduer før vi kommer, så der ikke kommer vand ind.</li>
                    {isInside &&
                        <>
                            <li>• Området foran dine indendørs vinduer skal være ryddet.</li>
                            <li>• Indvendige vinduer skal kunne nås med trappestige.</li>
                        </>
                    }
                    <li>
                        {isInside
                            ? '• Udvendigt tager vi'
                            : '• Vi tager'
                        }
                        &nbsp;ikke vinduer, der er over garage, karnap,
                        restauranter/bar, butik eller lignende.
                    </li>
                </ul>
            </div>
            <div className={orderStyles.orderSection}>
                <h1 className={orderStyles.cardH1}>
                    Note til vinduespudseren
                </h1>
                <p
                    className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}
                >
                    Er der noget særligt, vi bør vide om din bolig eller dine vinduer?
                </p>
                <textarea
                    className={orderStyles.textField}
                    maxLength={1000}
                    inputMode={'text'}
                    value={generalNote}
                    placeholder={'Indtast note - valgfri'}
                    onInput={event => {
                        const extraServiceCountInput = event.target as HTMLInputElement;
                        const value = extraServiceCountInput.value;
                        dispatch(windowCleaningActions.setGeneralNote(value));
                    }}
                ></textarea>
            </div>
            {serviceResult && frequency && priceResult &&
                <div className={orderStyles.orderSection}>
                    <h1 className={orderStyles.cardH1}>
                        Prisoversigt
                    </h1>
                    <div className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                        <p>
                            Din pris per besøg er {formattedPrice} efter rabat.
                        </p>
                        <p>
                            Du kan desuden med fordel benytte dig af servicefradraget på op til 11.900 kr.
                            fradrag pr. person. <a
                            className={orderStyles.hyperlink}
                            target={'_blank'}
                            href={'https://skat.dk/borger/fradrag/servicefradrag/servicefradrag'}
                        >
                            (Læs mere på skat.dk)
                        </a>
                        </p>
                        <a
                            className={orderStyles.hyperlink}
                            rel="noopener noreferrer"
                            onClick={() => dispatch(windowCleaningActions.togglePriceExpanded())}
                        >
                            Vis detaljeret prisoversigt
                        </a>
                    </div>
                </div>
            }
            <div
                className={orderStyles.orderSection}
            >
                <h1 className={orderStyles.cardH1}>
                    Betaling
                </h1>
                {(frequency?.every ?? 0) == 0
                    ? <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                        Engangsbesøg trækkes automatisk efter endt udførsel.
                    </p>
                    : <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                        Du betaler dine besøg en måned forud. Betalingen trækkes automatisk fra din indtastede
                        betalingsløsning.
                    </p>
                }
                <div className={`${orderStyles.orderSectionContent} ${orderStyles.flexRow} ${orderStyles.flexWrap}`}>
                    {paymentMethodAvailabilityResult?.available ?
                        <>
                            <p>
                                Du har allerede en tilknyttet betalingsmetode, der vil blive brugt. Du kan til enhver
                                tid ændre din betalingsmetode i app&apos;en.
                            </p>
                            <div
                                className={`${orderStyles.flexRow} px-2`}
                            >
                                <CheckBoxCircle
                                    id="acceptedTOS"
                                    onToggle={setAcceptedTOS}
                                    data-cy={'accept-tos'}
                                    $initialCheck={acceptedTOS}
                                />
                                <Typography
                                    variant={'body'}
                                >
                                    Jeg har læst og accepterer Housekeeprs&nbsp;
                                    <a href={routes.privacyPolicy.href}
                                       rel="noopener noreferrer" target="_blank"
                                       className={orderStyles.hyperlink}>handelsbetingelser
                                    </a>
                                    &nbsp;og&nbsp;
                                    <a
                                        rel="noopener noreferrer"
                                        href={routes.bussPolicy.href}
                                        target="_blank"
                                        className={orderStyles.hyperlink}>privatlivspolitik
                                    </a>
                                </Typography>
                            </div>
                        </>
                        : <>
                            <div
                                className={`${orderStyles.cardPaymentOption} ${selectedPaymentType == 'card' ? orderStyles.selected : ''}`}
                                onClick={() => {
                                    dispatch(windowCleaningActions.setPaymentType('card'));
                                }}
                            >
                                <Image
                                    className={'mx-2'}
                                    src={PaymentCardOptions}
                                    alt={'Betal med kort'}
                                />
                            </div>
                            <div
                                className={`${orderStyles.cardPaymentOption} ${orderStyles.mobilepay} ${selectedPaymentType == 'mobilepaysubscriptions' ? orderStyles.selected : ''}`}
                                onClick={() => {
                                    dispatch(cleaningActions.setPaymentType('mobilepaysubscriptions'));
                                }}
                            >
                                <Image
                                    className={'mx-2'}
                                    src={MobilepayBlue}
                                    alt={'Betal med MobilePay'}
                                />
                            </div>
                            <div
                                className={`${orderStyles.flexRow} px-2`}
                            >
                                <CheckBoxCircle
                                    id="acceptedTOS"
                                    onToggle={setAcceptedTOS}
                                    data-cy={'accept-tos'}
                                    $initialCheck={acceptedTOS}
                                />
                                <Typography
                                    variant={'body'}
                                >
                                    Jeg har læst og accepterer Housekeeprs&nbsp;
                                    <a href={routes.privacyPolicy.href}
                                       rel="noopener noreferrer" target="_blank"
                                       className={orderStyles.hyperlink}>handelsbetingelser
                                    </a>
                                    &nbsp;og&nbsp;
                                    <a
                                        rel="noopener noreferrer"
                                        href={routes.bussPolicy.href}
                                        target="_blank"
                                        className={orderStyles.hyperlink}>privatlivspolitik
                                    </a>
                                </Typography>
                            </div>
                        </>
                    }
                </div>
            </div>
        </div>
        <div className={`${orderStyles.flexRow} ${orderStyles.flexMainSpaceBetween} self-stretch`}>
            <div
                className={`${orderStyles.buttonWrapper} ${orderStyles.outline} w-[200px]`}>
                <button
                    onClick={() => {
                        dispatch(windowCleaningActions.setPage(WindowCleaningPage.USER));
                        smoothScrollToTop();
                    }}
                >
                    Tilbage
                </button>
            </div>
            <div
                className={`${orderStyles.buttonWrapper} ${(maxPage < WindowCleaningPage.SUBMITTING_ORDER || !acceptedTOS) ? orderStyles.disabled : ''} w-[200px]`}>
                <button
                    onClick={() => {
                        createEventV2('begin_checkout', null);
                        dispatch(windowCleaningActions.setPage(WindowCleaningPage.SUBMITTING_ORDER));
                        smoothScrollToTop();
                    }}
                    disabled={maxPage < WindowCleaningPage.SUBMITTING_ORDER || !acceptedTOS}
                >
                    Bestil
                </button>
            </div>
        </div>
    </div>;
};

export default WindowCleaningPaymentCard;
